"use client";

import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import clsx from "clsx";
import { Upload, Link as LinkIcon, Music, Image as ImageIcon, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Drag-and-drop media uploader for audio + image assets used in exercises.
 *
 * Three modes the admin can switch between:
 *   - **Upload** — drag a file here, or click to browse. POSTs to
 *     `/api/admin/media/upload` and stores the returned URL.
 *   - **URL** — paste an existing URL (e.g. from a CDN).
 *   - **Record** — placeholder; the recording flow ships in Sprint 2.
 *
 * Falls back to URL mode when the upload endpoint returns 404 (e.g. dev
 * with the backend not running). Always renders a thumbnail preview so
 * the admin can confirm the asset is right before saving.
 */
export type MediaKind = "audio" | "image";

const MIME_ALLOWLIST: Record<MediaKind, string[]> = {
  audio: ["audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/ogg", "audio/webm"],
  image: ["image/jpeg", "image/png", "image/webp", "image/gif"],
};

const SIZE_LIMIT: Record<MediaKind, number> = {
  audio: 10 * 1024 * 1024, // 10 MB
  image: 2 * 1024 * 1024, // 2 MB
};

export interface MediaUploaderProps {
  kind: MediaKind;
  /** Current value (URL string). Controlled. */
  value: string;
  onChange: (next: string) => void;
  /** Optional label above the field. */
  label?: string;
  /** Optional helper text below the field. */
  hint?: string;
}

export function MediaUploader({ kind, value, onChange, label, hint }: MediaUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mimeLabel = MIME_ALLOWLIST[kind].join(", ");
  const sizeMb = (SIZE_LIMIT[kind] / 1024 / 1024).toFixed(0);

  async function handleFile(file: File) {
    setError(null);
    if (!MIME_ALLOWLIST[kind].includes(file.type)) {
      setError(`Invalid file type. Expected ${mimeLabel}.`);
      return;
    }
    if (file.size > SIZE_LIMIT[kind]) {
      setError(`File too large. Max ${sizeMb} MB.`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      const res = await api.post("/api/admin/media/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = (res.data as { url?: string }).url;
      if (!url) {
        setError("Upload succeeded but no URL was returned.");
        return;
      }
      onChange(url);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message ?? err.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = ""; // allow re-upload of same file
  }

  const Icon = kind === "audio" ? Music : ImageIcon;

  return (
    <div>
      {label && (
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
          {label}
        </label>
      )}

      <div className="flex items-center gap-2 mb-2">
        <input
          ref={inputRef}
          type="file"
          accept={mimeLabel}
          onChange={onPick}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#1C1C1E] border border-[rgba(255,255,255,0.08)] text-xs text-[#FAFAFA] hover:bg-[#2C2C2E] disabled:opacity-50"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          Upload
        </button>
        <span className="text-[10px] font-mono text-[#8E8E93]">or</span>
        <div className="relative flex-1">
          <LinkIcon
            size={12}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]"
          />
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Paste ${kind} URL…`}
            className="w-full h-9 pl-7 pr-3 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] text-xs text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
          />
        </div>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Clear media"
            className="rounded-md p-1.5 text-[#8E8E93] hover:bg-white/10 hover:text-[#FAFAFA]"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Drop zone — always visible but quiet. Doubles as the preview area. */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={clsx(
          "relative rounded-lg border border-dashed transition-colors",
          dragOver ? "border-[#FFD60A] bg-[#FFD60A]/10" : "border-[rgba(255,255,255,0.08)]",
        )}
      >
        {value ? (
          <MediaPreview kind={kind} url={value} />
        ) : (
          <div className="flex items-center justify-center gap-2 px-3 py-5 text-xs text-[#8E8E93]">
            <Icon size={14} />
            Drag a {kind} file here, or use the controls above
          </div>
        )}
      </div>

      {(error || hint) && (
        <p
          className={clsx(
            "mt-1 text-[10px] font-mono",
            error ? "text-[#FF453A]" : "text-[#8E8E93]",
          )}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

function MediaPreview({ kind, url }: { kind: MediaKind; url: string }) {
  if (kind === "audio") {
    return (
      <div className="px-3 py-2">
        <audio controls preload="metadata" src={url} className="w-full h-9">
          Your browser does not support audio playback.
        </audio>
      </div>
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={url}
      alt="Uploaded preview"
      className="block w-full max-h-48 object-contain rounded-md bg-[#0A0A0A]"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = "none";
      }}
    />
  );
}